import { useLazyQuery, useMutation } from '@apollo/client';

import {
  GetDreamMachineProfileSkillsDocument,
  UpdateDreamMachineSkillsDocument,
  type UpdateDreamMachineSkillsMutation,
  type UpdateDreamMachineSkillsMutationVariables,
} from '@xing-com/crate-dream-machine-graphql';
import { useDreamTracking } from '@xing-com/crate-dream-machine-tracking';
import { useViewerData } from '@xing-com/hub';

import { useSearchAndShuffleContext } from '../../context';

type UseSkillsReturnType = {
  addSkills: (skills: string[]) => Promise<void>;
};

export const useSkills = (): UseSkillsReturnType => {
  const { setStatusBanner, decks, setDecks } = useSearchAndShuffleContext();
  const { trackSkillsAddedSuccess, trackSkillsAddedFail } = useDreamTracking();
  const viewerData = useViewerData();
  const userId = viewerData.data?.user?.id;

  const [getSkills] = useLazyQuery(GetDreamMachineProfileSkillsDocument, {
    fetchPolicy: 'no-cache',
  });

  const [updateSkills] = useMutation<
    UpdateDreamMachineSkillsMutation,
    UpdateDreamMachineSkillsMutationVariables
  >(UpdateDreamMachineSkillsDocument, { errorPolicy: 'all' });

  const handleError = (): void => {
    setStatusBanner({
      show: true,
      type: 'error',
      key: 'DREAM_MACHINE_ERROR_GENERIC_STATUS_BANNER',
    });
    trackSkillsAddedFail();
  };

  const handleSuccess = (hadSkillsAlready: boolean): void => {
    setStatusBanner({
      show: true,
      type: 'success',
      key: 'DREAM_MACHINE_JOB_CARD_SKILLS_PROFILE_UPDATED',
    });
    trackSkillsAddedSuccess(hadSkillsAlready);
  };

  const updateSkillsCache = (skills: string[]): void => {
    const updatedDecks = decks;
    updatedDecks.forEach((deck) => {
      deck.cards.forEach((card) => {
        if (
          (card.type === 'AvailableJob' || card.type === 'TakenJob') &&
          card?.data?.skillsMatched
        ) {
          card.data.skillsMatched = [...card.data.skillsMatched, ...skills];
          card.data.skillsNotMatched = card.data.skillsNotMatched?.filter(
            (skill) => !skills.includes(skill || '')
          );
          card.data.skillsMatchedCount = card.data.skillsMatched.length;
          card.data.skillsNotMatchedCount =
            card.data.skillsNotMatched?.length || 0;
        }
      });
    });

    setDecks(updatedDecks);
  };

  const addSkills = async (skills: string[]): Promise<void> => {
    if (!userId) {
      return;
    }

    const { data, error: getSkillsError } = await getSkills({
      variables: { userId: userId },
    });

    const currentSkills =
      data?.profileSkills?.collection
        ?.filter((item) => item !== null)
        .map(({ __typename, ...item }) => item) || [];

    if (getSkillsError) {
      handleError();
      return;
    }

    const { data: dataUpdateSkills, errors: updateSkillsErrors } =
      await updateSkills({
        variables: {
          input: {
            shareWithContacts: true,
            collection: [
              ...currentSkills,
              ...skills.map((skill) => ({
                value: skill,
                isTop: false,
              })),
            ],
          },
        },
      });

    if (
      updateSkillsErrors ||
      dataUpdateSkills?.profileSkillsUpdate?.error !== null
    ) {
      handleError();
      return;
    }
    updateSkillsCache(skills);
    handleSuccess(currentSkills.length > 0);
  };

  return { addSkills };
};
